"use client";

import { useId, useState } from "react";
import type { DateRange } from "react-day-picker";
import { PopoverCalendar } from "@/components/ui/popover-calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  earliestSelectableDeliveryDate,
  formatDeliveryWindow,
  latestSelectableDeliveryDate,
  latestSelectableDeliveryDateFromEarliest,
  parseDateKey,
  toDateKey,
  type DeliveryWindow,
} from "@/lib/checkout/delivery-window";
import { cn } from "@/lib/utils";

export function DeliveryWindowPicker({
  window,
  onWindowChange,
  placeholder = "Choose dates",
  className,
  labelledBy,
  earliestDate,
  children,
}: {
  window: DeliveryWindow | null;
  onWindowChange: (window: DeliveryWindow) => void;
  placeholder?: string;
  className?: string;
  labelledBy?: string;
  earliestDate?: Date;
  children?: React.ReactNode;
}) {
  const triggerId = useId();
  const { open, onOpenChange, draft, setDraft, close } = usePickerPopover();
  const selected = draft ?? toDateRange(window);
  const label = formatDeliveryWindow(window);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        {children ?? (
          <WindowTrigger
            triggerId={triggerId}
            labelledBy={labelledBy}
            label={label}
            placeholder={placeholder}
            className={className}
          />
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-0">
        <WindowCalendar
          earliestDate={earliestDate}
          selected={selected}
          onDraft={setDraft}
          onSettled={(settled) => {
            onWindowChange(settled);
            close();
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

function usePickerPopover() {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateRange | undefined>(undefined);
  return {
    open,
    draft,
    setDraft,
    onOpenChange: (next: boolean) => {
      if (!next) setDraft(undefined);
      setOpen(next);
    },
    close: () => {
      setDraft(undefined);
      setOpen(false);
    },
  };
}

function WindowCalendar({
  selected,
  onDraft,
  onSettled,
  earliestDate,
}: {
  selected: DateRange | undefined;
  onDraft: (range: DateRange | undefined) => void;
  onSettled: (window: DeliveryWindow) => void;
  earliestDate?: Date;
}) {
  const earliest = earliestDate ?? earliestSelectableDeliveryDate();
  const latest = earliestDate
    ? latestSelectableDeliveryDateFromEarliest(earliest)
    : latestSelectableDeliveryDate();
  return (
    <PopoverCalendar
      mode="range"
      min={1}
      selected={selected}
      defaultMonth={selected?.from}
      onSelect={(range) => {
        const settled = settleRange(range);
        if (settled) onSettled(settled);
        else onDraft(range);
      }}
      endMonth={latest}
      disabled={(date) => date < earliest || date > latest}
    />
  );
}

function WindowTrigger({
  triggerId,
  labelledBy,
  label,
  placeholder,
  className,
}: {
  triggerId: string;
  labelledBy?: string;
  label: string;
  placeholder: string;
  className?: string;
}) {
  return (
    <button
      type="button"
      id={triggerId}
      aria-labelledby={labelledBy ? `${labelledBy} ${triggerId}` : undefined}
      className={cn("text-left text-sm", !label && "text-muted-foreground", className)}
    >
      {label || placeholder}
    </button>
  );
}

function toDateRange(window: DeliveryWindow | null): DateRange | undefined {
  if (!window) return undefined;
  const from = parseDateKey(window.start);
  if (!from) return undefined;
  return { from, to: parseDateKey(window.end) ?? undefined };
}

function settleRange(range: DateRange | undefined): DeliveryWindow | null {
  if (!range?.from || !range.to) return null;
  if (range.to.getTime() === range.from.getTime()) return null;
  return { start: toDateKey(range.from), end: toDateKey(range.to) };
}
